/**
 * The document of the music video editor (spec `2026-10-09-music24-music-video-editor-design.md`
 * §4.1): one cut of a song. The clips on the song, the texts, the lyric captions, the look and the
 * render settings. The web editor, the director of music24-api and the plan builder use this one
 * contract.
 *
 * The beat grid, the sections and the lyric lines are NOT in the document: they come from the song
 * (the analysis, the song map, the lyric timing). A clip edge snaps to a beat in the editor only;
 * the document keeps seconds.
 *
 * `parseMusicVideoDoc` is the one check of a document. It clamps every field, drops unknown keys,
 * sorts the clips and makes them cover the song from `settings.startSec` to `settings.endSec` with
 * no gap and no overlap. It repairs the effects with `parseMusicVideoEffects`. The file has no heavy
 * imports.
 */
import { parseMusicVideoEffects, type MusicVideoEffect } from './musicVideoEffects';
import {
  DEFAULT_MUSIC_VIDEO_GRADE,
  isMusicVideoCutIn,
  isMusicVideoFit,
  isMusicVideoGrade,
  isMusicVideoMove,
  isMusicVideoSpeed,
  MUSIC_VIDEO_END_CARD_LINES,
  MUSIC_VIDEO_LYRIC_PLACES,
  MUSIC_VIDEO_LYRIC_STYLES,
  MUSIC_VIDEO_LYRIC_WHEN,
  MUSIC_VIDEO_TEXT_MAX_LENGTH,
  type MusicVideoCutIn,
  type MusicVideoEndCardLine,
  type MusicVideoFit,
  type MusicVideoGrade,
  type MusicVideoLyricPlace,
  type MusicVideoLyricStyle,
  type MusicVideoLyricWhen,
  type MusicVideoMove,
  type MusicVideoSpeed,
} from './musicVideoLook';

export { MUSIC_VIDEO_TEXT_MAX_LENGTH };

export const MUSIC_VIDEO_DOC_VERSION = 1;
/** The largest stored document, as UTF-8 JSON. */
export const MUSIC_VIDEO_DOC_MAX_BYTES = 256 * 1024;
/** The most clips of one cut. A 15-minute song cut on every beat at 120 bpm stays under it. */
export const MUSIC_VIDEO_MAX_CLIPS = 600;
/** The most cuts of one song. The oldest cut that was never made goes first. */
export const MUSIC_VIDEO_CUTS_PER_SONG = 20;
/** The director runs (first cuts and recuts) of one song in one hour. */
export const MUSIC_VIDEO_RECUTS_PER_HOUR = 30;
/** The director runs (first cuts and recuts) of one artist in one hour, over every song. */
export const MUSIC_VIDEO_DIRECTOR_RUNS_PER_HOUR_PER_ACCOUNT = 60;
/** The length of a bank clip (an 8 s Veo clip). */
export const MUSIC_VIDEO_BANK_CLIP_SEC = 8;
/** The shortest part of a source that a clip plays from its in point: the in point stays this far before the end. */
export const MUSIC_VIDEO_MIN_SEGMENT_SEC = 0.2;
/** The shortest clip. A shorter clip goes, and the clip before it takes its time. */
export const MUSIC_VIDEO_MIN_CLIP_SEC = 0.25;
/** The shortest video (`settings.endSec - settings.startSec`). */
export const MUSIC_VIDEO_MIN_LENGTH_SEC = 1;
/** The most texts that the user adds. */
export const MUSIC_VIDEO_MAX_CUSTOM_TEXTS = 50;
/** The longest id (a clip, a custom text) and the longest uuid in a document. */
const MAX_ID_LENGTH = 64;

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

/** A clip of the bank (`BankClipModel`), or a footage clip of the account (`FootageClipModel`). */
export type MusicVideoClipSource = { kind: 'bank'; clipUuid: string } | { kind: 'footage'; clipUuid: string };

export interface MusicVideoClip {
  /** Stable in the document: the editor selects and moves a clip by it. */
  id: string;
  /** The place on the song, in seconds. */
  startSec: number;
  endSec: number;
  source: MusicVideoClipSource;
  /** The start inside the clip, in seconds. */
  inSec: number;
  speed: MusicVideoSpeed;
  move: MusicVideoMove;
  cutIn: MusicVideoCutIn;
  fit: MusicVideoFit;
  /** The point of the clip that stays in the frame (0..1). `fill` only. */
  cropX: number;
  cropY: number;
  /** A recut never replaces a kept clip. The own footage is kept by default. */
  keep: boolean;
}

/** A fixed text of the video (the title, the artist name). An empty `text` shows the song value. */
export interface MusicVideoTextToggle {
  on: boolean;
  text: string;
}

export interface MusicVideoEndCard {
  on: boolean;
  line: MusicVideoEndCardLine;
  /** The SmartLink of the end card. Null: the SmartLink of the song. */
  smartLinkUuid: string | null;
}

export interface MusicVideoCustomText {
  id: string;
  text: string;
  startSec: number;
  endSec: number;
}

export interface MusicVideoTexts {
  title: MusicVideoTextToggle;
  name: MusicVideoTextToggle;
  endCard: MusicVideoEndCard;
  /** The small "Artist · Title" in a corner. */
  songInfoCorner: boolean;
  custom: MusicVideoCustomText[];
}

export interface MusicVideoLyrics {
  on: boolean;
  style: MusicVideoLyricStyle;
  where: MusicVideoLyricPlace;
  when: MusicVideoLyricWhen;
}

export interface MusicVideoLookSettings {
  grade: MusicVideoGrade;
  grain: boolean;
  vignette: boolean;
  /** A flash on the first beat of each chorus. */
  flashOnDrop: boolean;
  /** Light leaks on the section changes. */
  lightLeaks: boolean;
  /** 0 = long shots, 1 = fast cuts. The director reads it; a hand edit does not change it. */
  cutSpeed: number;
}

export interface MusicVideoSettings {
  fadeIn: boolean;
  fadeOut: boolean;
  /** The part of the song that the video covers, in seconds. */
  startSec: number;
  endSec: number;
}

export interface MusicVideoDoc {
  version: 1;
  /** Sorted, no gaps, no overlap, from `settings.startSec` to `settings.endSec`. Empty while the director works. */
  clips: MusicVideoClip[];
  texts: MusicVideoTexts;
  lyrics: MusicVideoLyrics;
  look: MusicVideoLookSettings;
  settings: MusicVideoSettings;
  /**
   * The motion design over the clips (`musicVideoEffects.ts`): lyric moments, title cards,
   * transitions, shot effects. Sorted by start. A stored doc with no `effects` key reads as an empty
   * list; the version stays 1, because the native apps refuse every other version.
   */
  effects: MusicVideoEffect[];
}

/** Why a document was refused. */
export type MusicVideoDocRefusal =
  | 'notObject'
  | 'version'
  | 'duration'
  | 'clips'
  | 'tooManyClips'
  | 'clipSource'
  | 'clipId'
  | 'tooManyTexts';

export type ParseMusicVideoDocResult = { ok: true; doc: MusicVideoDoc } | { ok: false; reason: MusicVideoDocRefusal };

/** The chips of the recut box (design c6). The director reads them as instructions. */
export const RECUT_CHIPS = ['fasterCuts', 'calmer', 'moreOfMyFootage', 'noPeople', 'brighter'] as const;
export type RecutChip = (typeof RECUT_CHIPS)[number];

// ---------------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------------

/** A new cut of a song with no clips: the director or the user fills it. */
export function defaultMusicVideoDoc(durationSec: number): MusicVideoDoc {
  const endSec = Number.isFinite(durationSec) && durationSec > 0 ? roundSec(durationSec) : 0;
  return {
    version: MUSIC_VIDEO_DOC_VERSION,
    clips: [],
    texts: {
      title: { on: true, text: '' },
      name: { on: true, text: '' },
      endCard: { on: true, line: 'auto', smartLinkUuid: null },
      songInfoCorner: false,
      custom: [],
    },
    lyrics: { on: false, style: 'clean', where: 'bottom', when: 'all' },
    look: {
      grade: DEFAULT_MUSIC_VIDEO_GRADE,
      grain: true,
      vignette: false,
      flashOnDrop: true,
      lightLeaks: false,
      cutSpeed: 0.5,
    },
    settings: { fadeIn: true, fadeOut: true, startSec: 0, endSec },
    effects: [],
  };
}

// ---------------------------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Seconds kept to the millisecond, so a document stays small and compares well. */
function roundSec(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function idOf(value: unknown): string | null {
  return typeof value === 'string' && value !== '' && value.length <= MAX_ID_LENGTH ? value : null;
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, MUSIC_VIDEO_TEXT_MAX_LENGTH) : '';
}

function parseSource(value: unknown): MusicVideoClipSource | null {
  if (!isRecord(value)) {
    return null;
  }
  const clipUuid = idOf(value.clipUuid);
  const { kind } = value;
  if (clipUuid === null || (kind !== 'bank' && kind !== 'footage')) {
    return null;
  }
  return { kind, clipUuid };
}

/** One clip with clamped fields, or the reason it is refused. The times are fixed later. */
function parseClip(
  value: unknown,
  range: { startSec: number; endSec: number }
): MusicVideoClip | 'clipSource' | 'clipId' {
  if (!isRecord(value)) {
    return 'clipSource';
  }
  const id = idOf(value.id);
  if (id === null) {
    return 'clipId';
  }
  const source = parseSource(value.source);
  if (source === null) {
    return 'clipSource';
  }
  const startSec = roundSec(clamp(finiteOr(value.startSec, range.startSec), range.startSec, range.endSec));
  const endSec = roundSec(clamp(finiteOr(value.endSec, startSec), range.startSec, range.endSec));
  const { speed, move, cutIn, fit } = value;
  return {
    id,
    startSec,
    endSec,
    source,
    inSec: roundSec(Math.max(0, finiteOr(value.inSec, 0))),
    speed: isMusicVideoSpeed(speed) ? speed : 1,
    move: isMusicVideoMove(move) ? move : 'still',
    cutIn: isMusicVideoCutIn(cutIn) ? cutIn : 'cut',
    fit: isMusicVideoFit(fit) ? fit : 'fill',
    cropX: clamp(finiteOr(value.cropX, 0.5), 0, 1),
    cropY: clamp(finiteOr(value.cropY, 0.5), 0, 1),
    keep: booleanOr(value.keep, source.kind === 'footage'),
  };
}

/**
 * Sorts the clips and makes them cover `range` with no gap and no overlap:
 * - an overlap: the later clip starts where the clip before ends;
 * - a gap: the clip before ends where the next clip starts;
 * - the first clip starts at `range.startSec`, the last clip ends at `range.endSec`;
 * - a clip shorter than `MUSIC_VIDEO_MIN_CLIP_SEC` after this goes.
 * The order of equal starts stays the order of the input.
 */
export function normaliseMusicVideoClips(
  clips: readonly MusicVideoClip[],
  range: { startSec: number; endSec: number }
): MusicVideoClip[] {
  const sorted = clips
    .map((clip, index) => ({ clip, index }))
    .sort((a, b) => {
      const byStart = a.clip.startSec - b.clip.startSec;
      return byStart !== 0 && !Number.isNaN(byStart) ? byStart : a.index - b.index;
    })
    .map(({ clip }) => ({ ...clip }));

  const out: MusicVideoClip[] = [];
  for (const clip of sorted) {
    const previous = out[out.length - 1];
    if (previous !== undefined && clip.startSec < previous.endSec) {
      clip.startSec = previous.endSec;
    }
    if (clip.endSec - clip.startSec < MUSIC_VIDEO_MIN_CLIP_SEC) {
      continue;
    }
    if (previous !== undefined && clip.startSec > previous.endSec) {
      previous.endSec = clip.startSec;
    }
    out.push(clip);
  }

  const first = out[0];
  const last = out[out.length - 1];
  if (first !== undefined && last !== undefined) {
    first.startSec = range.startSec;
    last.endSec = range.endSec;
  }
  return out.map((clip) => ({ ...clip, startSec: roundSec(clip.startSec), endSec: roundSec(clip.endSec) }));
}

function parseTexts(value: unknown, range: { startSec: number; endSec: number }, base: MusicVideoTexts) {
  const texts: Record<string, unknown> = isRecord(value) ? value : {};
  const toggle = (raw: unknown, fallback: MusicVideoTextToggle): MusicVideoTextToggle =>
    isRecord(raw) ? { on: booleanOr(raw.on, fallback.on), text: textOf(raw.text).trim() } : fallback;
  const endCard: Record<string, unknown> = isRecord(texts.endCard) ? texts.endCard : {};
  const rawCustom = Array.isArray(texts.custom) ? texts.custom : [];
  if (rawCustom.length > MUSIC_VIDEO_MAX_CUSTOM_TEXTS) {
    return 'tooManyTexts' as const;
  }
  const seen = new Set<string>();
  const custom: MusicVideoCustomText[] = [];
  for (const raw of rawCustom) {
    if (!isRecord(raw)) {
      continue;
    }
    const id = idOf(raw.id);
    const text = textOf(raw.text).trim();
    if (id === null || seen.has(id) || text === '') {
      continue;
    }
    const startSec = roundSec(clamp(finiteOr(raw.startSec, range.startSec), range.startSec, range.endSec));
    const endSec = roundSec(clamp(finiteOr(raw.endSec, startSec), startSec, range.endSec));
    if (endSec - startSec < MUSIC_VIDEO_MIN_CLIP_SEC) {
      continue;
    }
    seen.add(id);
    custom.push({ id, text, startSec, endSec });
  }
  custom.sort((a, b) => a.startSec - b.startSec);
  const result: MusicVideoTexts = {
    title: toggle(texts.title, base.title),
    name: toggle(texts.name, base.name),
    endCard: {
      on: booleanOr(endCard.on, base.endCard.on),
      line: oneOf(endCard.line, MUSIC_VIDEO_END_CARD_LINES, base.endCard.line),
      smartLinkUuid: idOf(endCard.smartLinkUuid),
    },
    songInfoCorner: booleanOr(texts.songInfoCorner, base.songInfoCorner),
    custom,
  };
  return result;
}

/**
 * Checks a document from a client or from the database against a song of `durationSec` seconds.
 * Unknown keys go, every field is clamped or gets its default, the clips are normalised
 * (`normaliseMusicVideoClips`). A value that cannot be repaired (not an object, another version,
 * too many clips, a clip with no id or no source) is refused with its reason.
 */
export function parseMusicVideoDoc(input: unknown, options: { durationSec: number }): ParseMusicVideoDocResult {
  const { durationSec } = options;
  if (!Number.isFinite(durationSec) || durationSec < MUSIC_VIDEO_MIN_LENGTH_SEC) {
    return { ok: false, reason: 'duration' };
  }
  if (!isRecord(input)) {
    return { ok: false, reason: 'notObject' };
  }
  if (input.version !== MUSIC_VIDEO_DOC_VERSION) {
    return { ok: false, reason: 'version' };
  }
  const base = defaultMusicVideoDoc(durationSec);

  const rawSettings: Record<string, unknown> = isRecord(input.settings) ? input.settings : {};
  const songEnd = roundSec(durationSec);
  const startSec = roundSec(clamp(finiteOr(rawSettings.startSec, 0), 0, songEnd - MUSIC_VIDEO_MIN_LENGTH_SEC));
  const endSec = roundSec(clamp(finiteOr(rawSettings.endSec, songEnd), startSec + MUSIC_VIDEO_MIN_LENGTH_SEC, songEnd));
  const settings: MusicVideoSettings = {
    fadeIn: booleanOr(rawSettings.fadeIn, base.settings.fadeIn),
    fadeOut: booleanOr(rawSettings.fadeOut, base.settings.fadeOut),
    startSec,
    endSec,
  };
  const range = { startSec, endSec };

  if (input.clips !== undefined && !Array.isArray(input.clips)) {
    return { ok: false, reason: 'clips' };
  }
  const rawClips: unknown[] = Array.isArray(input.clips) ? input.clips : [];
  if (rawClips.length > MUSIC_VIDEO_MAX_CLIPS) {
    return { ok: false, reason: 'tooManyClips' };
  }
  const clips: MusicVideoClip[] = [];
  const ids = new Set<string>();
  for (const raw of rawClips) {
    const clip = parseClip(raw, range);
    if (typeof clip === 'string') {
      return { ok: false, reason: clip };
    }
    if (ids.has(clip.id)) {
      return { ok: false, reason: 'clipId' };
    }
    ids.add(clip.id);
    clips.push(clip);
  }

  const texts = parseTexts(input.texts, range, base.texts);
  if (texts === 'tooManyTexts') {
    return { ok: false, reason: 'tooManyTexts' };
  }

  const rawLyrics: Record<string, unknown> = isRecord(input.lyrics) ? input.lyrics : {};
  const rawLook: Record<string, unknown> = isRecord(input.look) ? input.look : {};
  const { grade } = rawLook;
  return {
    ok: true,
    doc: {
      version: MUSIC_VIDEO_DOC_VERSION,
      clips: normaliseMusicVideoClips(clips, range),
      texts,
      lyrics: {
        on: booleanOr(rawLyrics.on, base.lyrics.on),
        style: oneOf(rawLyrics.style, MUSIC_VIDEO_LYRIC_STYLES, base.lyrics.style),
        where: oneOf(rawLyrics.where, MUSIC_VIDEO_LYRIC_PLACES, base.lyrics.where),
        when: oneOf(rawLyrics.when, MUSIC_VIDEO_LYRIC_WHEN, base.lyrics.when),
      },
      look: {
        grade: isMusicVideoGrade(grade) ? grade : base.look.grade,
        grain: booleanOr(rawLook.grain, base.look.grain),
        vignette: booleanOr(rawLook.vignette, base.look.vignette),
        flashOnDrop: booleanOr(rawLook.flashOnDrop, base.look.flashOnDrop),
        lightLeaks: booleanOr(rawLook.lightLeaks, base.look.lightLeaks),
        cutSpeed: clamp(finiteOr(rawLook.cutSpeed, base.look.cutSpeed), 0, 1),
      },
      settings,
      effects: parseMusicVideoEffects(input.effects, durationSec),
    },
  };
}

function utf8Length(text: string): number {
  let bytes = 0;
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else {
      bytes += code < 0x10000 ? 3 : 4;
    }
  }
  return bytes;
}

/** True when the document fits the stored limit (`MUSIC_VIDEO_DOC_MAX_BYTES` of UTF-8 JSON). */
export function isMusicVideoDocSizeOk(doc: MusicVideoDoc): boolean {
  return utf8Length(JSON.stringify(doc)) <= MUSIC_VIDEO_DOC_MAX_BYTES;
}

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

/**
 * The clip that plays at `sec` of the song. A clip holds its start and not its end; the end of the
 * video belongs to the last clip. Null outside the clips. The clips must be sorted (a parsed doc).
 */
export function clipAt(doc: Pick<MusicVideoDoc, 'clips'>, sec: number): MusicVideoClip | null {
  const { clips } = doc;
  let low = 0;
  let high = clips.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const clip = clips[middle];
    if (clip === undefined) {
      return null;
    }
    if (sec < clip.startSec) {
      high = middle - 1;
    } else if (sec >= clip.endSec) {
      low = middle + 1;
    } else {
      return clip;
    }
  }
  const last = clips[clips.length - 1];
  return last !== undefined && sec === last.endSec ? last : null;
}

/**
 * The beat nearest to `sec` when it is at most `toleranceSec` away, else `sec`. `beats` are the
 * beat times of the song map, sorted.
 */
export function snapToBeat(sec: number, beats: readonly number[], toleranceSec: number): number {
  if (beats.length === 0 || !(toleranceSec >= 0)) {
    return sec;
  }
  let low = 0;
  let high = beats.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if ((beats[middle] ?? Infinity) < sec) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  let best: number | null = null;
  for (const candidate of [beats[low - 1], beats[low]]) {
    if (candidate === undefined) {
      continue;
    }
    if (best === null || Math.abs(candidate - sec) < Math.abs(best - sec)) {
      best = candidate;
    }
  }
  return best !== null && Math.abs(best - sec) <= toleranceSec ? best : sec;
}

/** A section of the song map, as the editor and the director number them. */
export interface MusicVideoSectionSpan {
  index: number;
  startSec: number;
  endSec: number;
}

/**
 * The clips of the picked sections (a recut replaces them). A clip belongs to the section that
 * holds its middle. `'all'` picks every section.
 */
export function clipsInSections(
  doc: Pick<MusicVideoDoc, 'clips'>,
  sections: readonly MusicVideoSectionSpan[],
  picked: readonly number[] | 'all'
): MusicVideoClip[] {
  const chosen = picked === 'all' ? sections : sections.filter((section) => picked.includes(section.index));
  return doc.clips.filter((clip) => {
    const middle = (clip.startSec + clip.endSec) / 2;
    return chosen.some((section) => middle >= section.startSec && middle < section.endSec);
  });
}

/**
 * The longest song time that a clip can fill from its in point before its source ends:
 * `(clipLengthSec - inSec) / speed`. A bank clip is 8 s; a footage clip has its `durationSec`.
 * The plan builder starts the clip again from in point 0 after this time (a loop), so the web can
 * show where the loop starts. A bad length or speed gives 0. Pure.
 */
export function maxClipSpanSec(clipLengthSec: number, inSec: number, speed: number): number {
  if (!Number.isFinite(clipLengthSec) || !Number.isFinite(inSec) || !Number.isFinite(speed) || speed <= 0) {
    return 0;
  }
  return Math.max(0, (clipLengthSec - Math.max(0, inSec)) / speed);
}

/**
 * The latest in point of a clip in a source of `clipLengthSec` (a bank clip is
 * `MUSIC_VIDEO_BANK_CLIP_SEC`; a footage clip has its `durationSec`): `clipLengthSec -
 * MUSIC_VIDEO_MIN_SEGMENT_SEC`. The one in-point limit of the stored plan and of the make check; a
 * clip also stops where its source ends (`maxClipSpanSec`). A bad length gives 0. Pure.
 */
export function maxClipInSec(clipLengthSec: number): number {
  if (!Number.isFinite(clipLengthSec)) {
    return 0;
  }
  return Math.max(0, Math.round((clipLengthSec - MUSIC_VIDEO_MIN_SEGMENT_SEC) * 1000) / 1000);
}
